import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import gsap from "gsap";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { useUser } from '../redux/useUser';
import { showSuccessToast, showErrorToast } from "../components/Toast";
import { webAuth } from '../utils/auth0'; // Import webAuth from utils
import { api } from '../utils/api';
import countryCodes from '../utils/countryCodes.json';
import CountrySelectReact from '../utils/CountrySelect';
import { MailIcon, PhoneIcon, Smartphone } from "lucide-react";

const EmailPhoneLink = ({ hasSms, hasMultipleAuth0, auth0Data, onRequestPhoneUpdate }) => {

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState(''); // keep for legacy, but use code everywhere
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [showMsgModal, setShowMsgModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [step, setStep] = useState('initial');
  const [accessToken, setAccessToken] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const layersRef = useRef([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showPhoneLogin, setShowPhoneLogin] = useState(false);
  const [activeTab, setActiveTab] = useState('phone'); // 'email' or 'phone'
  const [showSignup, setShowSignup] = useState(false); // New state for controlling signup view
  const [signupTouched, setSignupTouched] = useState({ name: false, email: false, password: false });
    const [phoneTouched, setPhoneTouched] = useState(false);
    const [phoneError, setPhoneError] = useState('');
const [unlinking, setUnlinking] = useState(false);
  const [code, setCode] = useState('+91'); // use this for country code, will be auto-detected

// E.164-like phone validation: + followed by 10-15 digits
  const isValidE164 = (v) => {
    if (!v || typeof v !== 'string') return false;
    return /^\+[0-9]{10,15}$/.test(v.trim());
  };


const handleUnlinkIdentity = async (identity, e) => {
  if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
  if (!identity) return;

  if (unlinking) return; // prevent double click
  setUnlinking(true);

  try {
    const payload = {
      provider: identity.provider || null,
      userId: identity.userId || null,
      connection: identity.connection || null,
      isSocial: !!identity.isSocial,
      identifier: identity.identifier || null,
    };


    const accessId = localStorage.getItem("access_token");
    const res = await api.post("/users/remove-identity", payload, {
      headers: { Authorization: `Bearer ${accessId}` },
    });

    if (res?.status === 200) {
      const unlinkedLabel = identity.connection === 'sms' ? 'Phone number' : (identity.connection || identity.provider);
      showSuccessToast(`${unlinkedLabel} unlinked successfully.`);
      
      // If unlinking phone, clear the phone_verified flag so modal shows with proper state
      // Also set a flag to indicate phone was previously linked (for unlink detection)
      if (identity.connection === 'sms') {
        localStorage.removeItem('phone_verified');
        localStorage.setItem('phone_was_linked', 'true');
        
        try {
          // First get the user to get the internal user ID
          const userResponse = await api.get('/users/me', {
            headers: { Authorization: `Bearer ${accessId}` },
          });
          
          
          // Get user ID from response - check various possible locations
          const userData = userResponse?.data;
          const userId = userData?.user?.id || userData?.id || userData?._id;
          
          
          if (userId) {
            // Use PATCH /users/{userId} with phone field
            await api.patch(`/users/${userId}`, { phone: null }, {
              headers: { Authorization: `Bearer ${accessId}` },
            });
            console.log('User phone number removed successfully');
          } else {
            console.warn('Could not find user ID in response');
          }
        } catch (updateErr) {
          console.warn('Failed to remove user phone number:', updateErr);
        }
      }
      
      // refresh user so UI updates
      const token = localStorage.getItem("access_token");
      if (token) await getUser(token);
      
      // Redirect to Dashboard after successful unlink
      navigate('/dashboard');
    } else {
      showErrorToast("Failed to unlink account");
    }
  } catch (err) {
    console.error("Unlink failed", err);
    showErrorToast("Failed to unlink account. Try again.");
  } finally {
    setUnlinking(false);
  }
};
  const navigate = useNavigate();
  // Redux user hook
  const { user, loading: userLoading, error: userError, getUser } = useUser();
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotEmailError, setForgotEmailError] = useState("");
    const [resendLoading, setResendLoading] = useState(false);

  // Timer for resend code button
  const [resendTimer, setResendTimer] = useState(0);
  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    } else if (interval) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Helper to call remove-identity API if needed after add-identity
  const handleRemoveIdentityIfNeeded = async (hasSmsArray) => {
    if (!Array.isArray(hasSmsArray)) {
      return;
    }

    // total sms identities count
    const smsCount = hasSmsArray.filter(i => i.connection === "sms").length;
    if (smsCount >= 2) {
      const smsIdentityToRemove = hasSmsArray.find(i => i.connection === "sms");

      if (!smsIdentityToRemove) {

        return;
      }
      localStorage.setItem("smsIdentity", JSON.stringify(smsIdentityToRemove));
      localStorage.setItem("update_phone_userId", smsIdentityToRemove.userId);
      const payload = {
        identifier: smsIdentityToRemove.identifier,
        provider: smsIdentityToRemove.provider,
        userId: smsIdentityToRemove.userId,
        connection: smsIdentityToRemove.connection,
        isSocial: smsIdentityToRemove.isSocial ?? false
      };

      try {
        const accessId = localStorage.getItem("access_token");
        if (!accessId) {
          console.error("No access token in localStorage");
          return;
        }

        const response = await api.post("/users/remove-identity", payload, {
          headers: {
            Authorization: `Bearer ${accessId}`
          }
        });

        if (response && response.status === 200) {
          await onRequestPhoneUpdate();
          localStorage.removeItem("smsIdentity");
          localStorage.removeItem("update_phone_userId");
          // Refresh user data after removal
          const token = localStorage.getItem("access_token");
          if (token) {
            getUser(token);
          }
        } else {
          console.warn("Unexpected response removing identity:", response);
        }
      } catch (error) {
        console.error("Error removing identity:", error);
      }
    } else {

    }
  };
// State for update email UI
const [showUpdateEmail, setShowUpdateEmail] = useState(false);
const [newEmail, setNewEmail] = useState("");
const [updateEmailLoading, setUpdateEmailLoading] = useState(false);
const [updateEmailError, setUpdateEmailError] = useState("");
const [updateEmailSuccess, setUpdateEmailSuccess] = useState("");

// Handler for update email API
const handleUpdateEmail = async () => {
  setUpdateEmailLoading(true);
  setUpdateEmailError("");
  setUpdateEmailSuccess("");
  try {
    const accessId = localStorage.getItem('access_token');
    // if (!auth0UserId) {
    //   setUpdateEmailError("User ID not found");
    //   setUpdateEmailLoading(false);
    //   return;
    // }
    const response = await api.post(`/users/${user.id}/update-email`, { email: newEmail }, {
      headers: {
        Authorization: `Bearer ${accessId}`,
      },
    });
    setUpdateEmailSuccess("Email updated successfully!");
    setShowUpdateEmail(false);
    setNewEmail("");
     const token = localStorage.getItem('access_token');
        if (token) getUser(token);
    // After success, call remove-identity API as per logic
    await handleRemoveIdentityIfNeeded(Array.isArray(hasSms) ? hasSms : []);
  } catch (err) {
    setUpdateEmailError(err?.response?.data?.message || "Failed to update email");
  }
  setUpdateEmailLoading(false);
};
  // Automatically call remove-identity if hasSms contains two sms identities
  // Automatically call remove-identity if hasSms contains two sms identities
  useEffect(() => {
    if (Array.isArray(hasSms)) {
      const smsIdentities = hasSms.filter(i => i.connection === 'sms');
      if (smsIdentities.length === 2) {
        // Set userId in localStorage to the first sms identity's user_id
        if (smsIdentities[0]?.user_id) {
          localStorage.setItem('update_phone_userId', smsIdentities[0].user_id);
        }
        handleRemoveIdentityIfNeeded(hasSms);
      }
    }
  }, [hasSms]);
  useEffect(() => {
    const getRandom = (min, max) => Math.random() * (max - min) + min;
    const animateLayer = (layer, duration) => {
      gsap.fromTo(
        layer,
        {
          x: getRandom(-400, 400),
          y: getRandom(-400, 400),
          scale: getRandom(1.1, 1.6),
          opacity: getRandom(0.4, 0.7),
          rotation: getRandom(-20, 20),
        },
        {
          x: getRandom(-400, 400),
          y: getRandom(-400, 400),
          scale: getRandom(1.3, 1.8),
          opacity: getRandom(0.6, 0.9),
          rotation: getRandom(-20, 20),
          duration,
          repeat: -1,
          yoyo: true,
          ease: "power1.inOut",
        }
      );
    };
    layersRef.current.forEach((layer, index) => {
      const colors = [
        `radial-gradient(circle, rgba(255, 50, 150, 0.4), transparent 70%)`,
        `radial-gradient(circle, rgba(50, 150, 255, 0.4), transparent 70%)`,
        `radial-gradient(circle, rgba(150, 50, 255, 0.4), transparent 70%)`,
      ];
      layer.style.background = colors[index % colors.length];
      animateLayer(layer, getRandom(4, 6));
    });
  }, []);



  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  useEffect(() => {
    const detectCountryCode = async () => {
      try {
        // Try the original API first
        let response;
        try {
          response = await fetch("https://freeipapi.com/api/json");
        } catch (corsError) {
          // If CORS fails, try alternative APIs
          console.warn("CORS blocked, trying alternative API:", corsError);
          try {
            response = await fetch("https://ipapi.co/json/");
          } catch (altError) {
            console.warn("Alternative API failed, using default:", altError);
            setCode('+91'); // Default fallback
            return;
          }
        }
        
        const data = await response.json();
        const countryIso = data.countryCode || data.country_code || data.country;
        if (countryIso) {
          const country = countryCodes.find(c => c.code === countryIso);
          if (country && country.dial_code) {
            setCode(country.dial_code);
          } else {
            setCode('+91'); // Default if country not found
          }
        } else {
          setCode('+91'); // Default if no country data
        }
      } catch (error) {
        console.warn("Failed to detect country code:", error);
        setCode('+91'); // Default fallback
      }
    };
    detectCountryCode();
  }, []);

useEffect(() => {
  const error = sessionStorage.getItem('login_error');
  if (error) {
    showErrorToast(error);
    sessionStorage.removeItem('login_error');
  }
}, []);


  useEffect(() => {
    if (window.location.hash.includes('access_token')) {
    // handleAuthCallback();
  } else {
    // Try restoring from localStorage
    const storedToken = localStorage.getItem('access_token');
    const storedUser = localStorage.getItem('user_info');
    if (storedToken && storedUser) {
      setAccessToken(storedToken);
      setUserInfo(JSON.parse(storedUser));
      setStep('authenticated');
    }
  }
  }, []);

  const loginWithEmailPassword = () => {
    setLoading(true);
    setError('');
    webAuth.login({
      realm: 'Username-Password-Authentication',
      username: email,
      password,
    }, (err) => {
      setLoading(false);
      if (err) setError(err.description || 'Login failed');
      else {
        // Call getUser with token after login
        const token = localStorage.getItem('access_token');
        if (token) getUser(token);
        navigate('/dashboard');
      }
    });
  };

  const signupWithEmailPassword = () => {

    setLoading(true);
    setError('');
    webAuth.signup({
      connection: 'Username-Password-Authentication',
      email,
      password,
      name, 
      user_metadata: { name }
    }, (err) => {
      setLoading(false);
      if (err) {
        // Handle duplicate email error for all providers
        if (err.code === 'user_exists' || (err.description && err.description.toLowerCase().includes('user already exists'))) {
          setError('An account with this email already exists. Please login or use a different provider.');
        } else {
          setError(err.description || 'Signup failed');
        }
      }
      else {
        // After signup, login and then call getUser with token
        webAuth.login({
          realm: 'Username-Password-Authentication',
          username: email,
          password,
        }, (err) => {
          setLoading(false);
          if (err) setError(err.description || 'Login failed');
          else {
            const token = localStorage.getItem('access_token');
            if (token) getUser(token);
            navigate('/dashboard');
          }
        });
      }
    });
  };

  // Enhanced phone number formatting with validation
  const formatPhoneNumber = (phone, codeArg = code) => {
    // Remove all non-digit characters
    const cleanPhone = phone.replace(/\D/g, '');
    
    // If already starts with +, return as is
    if (phone.startsWith('+')) {
      return phone;
    }
    
    // Handle different country codes
    if (codeArg === '+91') {
      // US/Canada: remove leading 1 if present to avoid +1 1XXXXXXXXXX
      if (cleanPhone.startsWith('1') && cleanPhone.length === 11) {
        return codeArg + cleanPhone.substring(1);
      }
      // Ensure we have exactly 10 digits for US numbers
      if (cleanPhone.length === 10) {
        return codeArg + cleanPhone;
      }
    } else if (codeArg === '+91') {
      // India: should be 10 digits after country code
      if (cleanPhone.length === 10) {
        return codeArg + cleanPhone;
      }
    } else if (codeArg === '+44') {
      // UK: can be 10-11 digits after country code
      if (cleanPhone.length >= 10 && cleanPhone.length <= 11) {
        return codeArg + cleanPhone;
      }
    }
    
    // Default: just append country code
    return codeArg + cleanPhone;
  };

const sendOtp = () => {
 setPhoneTouched(true);
    if (!phoneNumber) {
      setPhoneError('Phone number is required');
      return;
    }
  if (!phoneNumber || phoneNumber.replace(/\D/g, '').length < 10) {
    setError('Please enter a valid phone number');
    return;
  }

  setLoading(true);
  setError('');
  setPhoneError('');
  const formattedPhone = formatPhoneNumber(phoneNumber, code);

  webAuth.passwordlessStart({
        connection: 'sms',
        phoneNumber: formattedPhone,
        send: 'code'
      }, (err) => {
        setLoading(false);
        if (err) {
          setPhoneError(err.description || 'Failed to send SMS');
        } else {
          setPhoneError('');
          setStep('verify-otp');
          setResendTimer(25);
        }
      });
};

const verifyOtp = () => {
    const currentToken = localStorage.getItem('access_token');
    if (currentToken) {
      localStorage.setItem('access_id', currentToken);
    }
    localStorage.setItem('linkaccount', 'true');
    localStorage.setItem('linkaccount_time', Date.now().toString());
  if (!otp || otp.length !== 6) {
    setError('Please enter a valid 6-digit OTP');
    return;
  }

  setLoading(true);
  setError('');
  const formattedPhone = formatPhoneNumber(phoneNumber, code);

  webAuth.passwordlessLogin({
    connection: 'sms',
    phoneNumber: formattedPhone,
    verificationCode: otp
  }, (err, authResult) => {
    setLoading(false);
    if (err) {
      setError(err.description || 'OTP verification failed');
    } else {

      setAccessToken(authResult.accessToken);
      localStorage.setItem('access_token', authResult.accessToken);
      setStep('authenticated');
      navigate('/dashboard');
    }
  });
};



  const loginWithSocial = (provider) => {
    const state = JSON.stringify({ returnTo: '/dashboard' });
    webAuth.authorize({
      connection: provider,
      redirectUri: window.location.origin + '/callback',
      responseType: 'token id_token',
      scope: 'openid profile email phone',
      state,
        // prompt: 'none',
    });
  };



  const resendOtp = () => {
    setOtp('');
    setError('');
    setResendLoading(true);
    setPhoneTouched(true);
    if (!phoneNumber) {
      setResendLoading(false);
       setPhoneTouched(true);
      setPhoneError('Phone number is required');
      return;
    }
    const formattedPhone = formatPhoneNumber(phoneNumber, code);
    if (!isValidE164(formattedPhone)) {
      setResendLoading(false);
      setPhoneError('Please enter a valid phone number');
      return;
    }
    webAuth.passwordlessStart({
      connection: 'sms',
      phoneNumber: formattedPhone,
      send: 'code'
    }, (err) => {
      setResendLoading(false);
      if (err) {
        setError(err.description || 'Failed to send SMS');
      } else {
        setResendTimer(25);
      }
    });
  };

 const sendResetLink = (customEmail) => {
  const useEmail = customEmail || email;
  if (!useEmail) {
    setError('Please enter your email to reset your password.');
    return;
  }
  setForgotPasswordLoading(true);
  webAuth.changePassword(
    {
      connection: 'Username-Password-Authentication',
      email: useEmail,
    },
    function (err, resp) {
      setForgotPasswordLoading(false);
      if (err) {
        console.error('Reset error:', err);
        setError('Failed to send reset email: ' + err.description);
      } else {
        setMsg('We’ve received your request. If an account exists for this email, you’ll receive a password reset link within the next few minutes.');
        setShowMsgModal(true);
      }
    }
  );
};

  const handleForgotSubmit = () => {
    if (!forgotEmail) {
      setForgotEmailError("Email is required");
      return;
    }
    setForgotEmailError("");
    setShowForgotModal(false);
    setEmail(forgotEmail); // set main email state
    sendResetLink(forgotEmail);
  };



// Prevent multiple getUser API calls: only call if token exists, user is not present, not loading, and userInfo is not present
useEffect(() => {
  const token = localStorage.getItem('access_token');
  if (token && !user && !loading && !userInfo) {
    getUser(token);
  }
}, [getUser, user, loading, userInfo]);

const smsIdentifier = hasSms.find(item => item.provider === "sms")?.identifier || null;
localStorage.setItem("update_phone", smsIdentifier);
  return (
    <>
      {/* TEST BUTTON: Remove after testing */}
      {/* <div style={{margin: '16px 0'}}>
        <Button variant="danger" onClick={() => handleRemoveIdentityIfNeeded(Array.isArray(hasSms) ? hasSms : [])}>
          Test Remove Identity
        </Button>
      </div>  */}

        {/* Right Section (Login Form) */}
        <Col className="auth-right">
          <div className="login-form card-body text-center d-flex flex-col justify-content-between h-100">
             {!showSignup && !showForgotModal &&(
            <div>
            <h6 className="fw-medium text-muted mb-3 text-sm leading-tight">Connect with</h6>
            <div className="d-flex mb-2 gap-2">
              {user?.auth0UserId?.split("|")[0] === "auth0" && (
              <Button
                variant={activeTab === 'email' ? 'solid-tab' : 'outline-tab'}
                className="flex-1 text-sm justify-content-center d-flex align-items-center btn-outline-changes-color"
                onClick={() => setActiveTab('email')}
              >
                <MailIcon size={16} />
                <span className="leading-snug">Email</span>
              </Button>
              )}
              <Button
                variant={activeTab === 'phone' ? 'solid-tab' : 'outline-tab'}
                className="flex-1 text-sm justify-content-center d-flex align-items-center btn-outline-changes-color"
                onClick={() => { setActiveTab('phone'); setError(''); }}
              >
                <Smartphone size={16} />
                <span className="leading-snug">Phone</span>
              </Button>
            </div>
            </div>
)}
            {/* Email/Password Login Section */}
            {activeTab === 'email' && (
              <div className="space-y-2">
                {!hasMultipleAuth0 ? (
                  <>
                    {/* ...existing code... */}
                    <Form.Group controlId="name" className="form-floating mb-3">
                      {/* ...existing code... */}
                    </Form.Group>
                    <Form.Group controlId="email" className="form-floating mb-3">
                      {/* ...existing code... */}
                    </Form.Group>
                    <Form.Group controlId="password" className="form-floating mb-0 position-relative">
                      {/* ...existing code... */}
                    </Form.Group>
                    {/* <div className="flex gap-2 mt-0 ">
                      <div className="flex flex-col items-end w-full">                     
                        <button 
                          onClick={() => {
                            // Save access_token as access_id, set linkaccount true, and save time
                            const accessToken = localStorage.getItem('access_token');
                            if (accessToken) {
                              localStorage.setItem('access_id', accessToken);
                            }
                            localStorage.setItem('linkaccount', 'true');
                            localStorage.setItem('linkaccount_time', Date.now().toString());
                            loginWithEmailPassword();
                          }}
                          disabled={loading || !email || !password}
                          className="px-4 py-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 disabled:bg-blue-300 transition-colors w-full mt-2"
                        >
                          {loading ? 'Login...' : 'Login'}
                        </button>
                      </div>
                    </div> */}
                  </>
                ):(
                  <div className="card-text text-muted text-center">
                   
                    {!showUpdateEmail && (
                      <>
                       Your email is successfully connected to your account.<br/> <br/>
                       {user?.auth0UserId?.split("|")[0] === "auth0" && (
                      <button
                        className="btn btn-primary manage-btn"
                        onClick={() => setShowUpdateEmail(true)}
                      >
                        Update Email
                      </button>
                       )}
                      </>
                    )}
                    {showUpdateEmail && (
                      <div className="mt-3">
                        <input
                          type="email"
                          className="form-control mb-2"
                          placeholder="Enter new email"
                          value={newEmail}
                          onChange={e => setNewEmail(e.target.value)}
                        />
                        <button
                          className="btn btn-primary manage-btn"
                          onClick={handleUpdateEmail}
                          disabled={updateEmailLoading || !newEmail}
                        >
                          {updateEmailLoading ? 'Updating...' : 'Submit'}
                        </button>
                        {updateEmailError && <div className="text-danger mt-2">{updateEmailError}</div>}
                        {updateEmailSuccess && <div className="text-success mt-2">{updateEmailSuccess}</div>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {/* Phone/OTP Login Section */}
            {activeTab === 'phone' && (
              <>
               {hasSms.length === 0 || showPhoneLogin ? (
              <div className="space-y-3">
                <Form.Group controlId="phone" className="form-floating mb-3 phone-input-group d-flex align-items-end gap-2">
                  <CountrySelectReact
                    value={code}
                    onChange={setCode}
                    maxWidth={130}
                    variant="underline"
                  />
                  <Form.Control
                    type="tel"
                    placeholder="Phone Number"
                    value={phoneNumber}
                    onChange={e => {
                      // Keep only numeric values - do not remove digits based on dial code
                      const input = e.target.value.replace(/\D/g, '').slice(0, 15);
                      setPhoneNumber(input);
                      setPhoneTouched(false);
                    }}
                    maxLength={15}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        setPhoneTouched(true);
                      }
                    }}
                    disabled={step === 'verify-otp'}
                    className="flex-1 pt-0 phone-number-input-underline"
                  />
                        {/* <Form.Label>Phone Number</Form.Label> */}
                      </Form.Group>
                      {/* {phoneTouched && phoneError && (
                        <div className="text-danger small mt-1">{phoneError}</div>
                        )} */}
                        {phoneTouched && phoneError === "undefined" && (
                    <div className="text-danger small mt-1">
                      Phone number does not match the selected dial code.
                    </div>
                  )}

                        {phoneTouched && !phoneError && (phoneNumber.length > 0 && phoneNumber.length < 10) && (
                          <div className="text-danger small mt-1">Please enter at least 10 digits</div>
                        )}
                        {phoneTouched && !phoneError && phoneNumber.length > 15 && (
                          <div className="text-danger small mt-1">Please enter at most 15 digits</div>
                        )}
                <p className="text-xs text-gray-500 lh-snug" style={{marginBottom: 10}}>
                  Enter your phone number without country code. We'll send you a verification code.
                </p>
                {step === 'verify-otp' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Verification Code</label>
                    <input 
                      type="text" 
                      placeholder="Enter 6-digit OTP" 
                      value={otp} 
                      onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} 
                      maxLength="6"
                      className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-300 focus:border-blue-500 text-center text-lg tracking-wide"
                      autoComplete="one-time-code"
                    />
                   <p className="text-sm text-gray-600 mt-1">
                     Check your phone for the 6-digit code we sent to <strong>{code} {phoneNumber}</strong>
                   </p>
                  </div>
                )}
                <button 
                  onClick={step === 'verify-otp' ? verifyOtp : sendOtp} 
                  disabled={loading || (step === 'initial' && !phoneNumber) || (step === 'verify-otp' && otp.length !== 6)}
                  className="btn btn-primary manage-btn"
                >
                  {loading ? 'Processing...' : (step === 'verify-otp' ? 'Verify OTP' : 'Send OTP')}
                </button>
                {step === 'verify-otp' && (
                  <div className="flex gap-2">
                    <button 
                      onClick={() => {
                        setStep('initial');
                        setOtp('');
                        setError('');
                      }}
                      className="btn btn-primary manage-btn"
                    >
                      Change Number
                    </button>
                    {resendTimer > 0 ? (
                      <div className="flex-1 text-sm text-secondary py-2 text-center" style={{opacity:0.7}}>
                        Resend in {resendTimer}s
                      </div>
                    ) : (
                      <button 
                        onClick={resendOtp}
                          disabled={resendLoading}
                        className="btn btn-primary manage-btn"
                      >
                          {resendLoading ? 'Sending...' : 'Resend Code'}
                      </button>
                    )}
                  </div>
                )}
              </div>
               ):(
                <div className="card-text text-muted text-center">
                 Your {smsIdentifier} is successfully connected to your account.<br/> <br/>
                 {user?.auth0UserId?.split("|")[0] !== "sms" && (
                 <button
                   className="btn btn-primary manage-btn"
                   onClick={() => {
                     // Store userId in localStorage when Update Phone Number is clicked
                     if (hasSms && typeof hasSms === 'object' && hasSms.userId) {
                       localStorage.setItem('update_phone_userId', hasSms.userId);
                     }
                     setShowPhoneLogin(true);
                   }}
                 >
                   Update Phone Number
                 </button>
                  )}

                  {user?.auth0UserId?.split("|")[0] !== "sms" && (
                    <>
                  {hasSms.length > 0 && (
    <button
      type="button"
      className="btn btn-primary manage-btn ms-2"
      onClick={(e) => handleUnlinkIdentity(hasSms[0], e)}
      disabled={unlinking}
    >
      {unlinking ? "Unlinking..." : "Unlink"}
    </button>
  )}
  </>
   )}
               </div>
               )}
               </>
            )}

          </div>
        </Col>

    </>
  );
};

export default EmailPhoneLink;
